import { ChangeDetectionStrategy, Component } from '@angular/core';

import { RevealDirective } from '@ledgerly/common/browser';

import { DeviceFrameComponent } from '../nocturne/device-frame.component';
import { ScreenTodayComponent } from '../nocturne/screen-today.component';
import { ScreenPlanComponent } from '../nocturne/screen-plan.component';
import { ScreenMoneyComponent } from '../nocturne/screen-money.component';
import { ScreenToolsComponent } from '../nocturne/screen-tools.component';
import type { TabId } from '../nocturne/tab-bar.component';

interface Point {
  title: string;
  detail: string;
}

interface Block {
  id: TabId;
  tab: string;
  headline: string;
  body: string;
  frameLabel: string;
  points: Point[];
}

/**
 * All four headlines are questions, because the intro promises four questions
 * and because that is genuinely how the app is arranged: each tab answers one
 * thing. An earlier version had a question on Today and statements on the
 * other three, which made the intro a lie about its own list.
 *
 * Copy rules, from CLAUDE.md:
 *
 * - Short sentences. One idea each.
 * - No em dashes. A full stop does the job.
 * - Plain words. Say what happens, not what it means.
 * - No aphorisms.
 * - Do not say "ledger". Say "your money" or "your records".
 *
 * And two facts nothing here may contradict: there is no bank connection and
 * no automatic categorisation.
 */
@Component({
  selector: 'app-features',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RevealDirective,
    DeviceFrameComponent,
    ScreenTodayComponent,
    ScreenPlanComponent,
    ScreenMoneyComponent,
    ScreenToolsComponent,
  ],
  templateUrl: './features.component.html',
})
export class FeaturesComponent {
  protected readonly blocks: Block[] = [
    {
      id: 'today',
      tab: $localize`:@@tabToday:Today`,
      headline: $localize`:@@todayHeadline:What can I spend today?`,
      body: $localize`:@@todayBody:Ledgerly takes what is left, subtracts what is still coming, and divides by the days that remain. You get one number, and the ones it came from are right underneath it.`,
      frameLabel: $localize`:@@todayFrameLabel:The Today screen. It shows what is safe to spend today, above the figures it was worked out from.`,
      points: [
        {
          title: $localize`:@@todayPoint1Title:Adding a spend takes seconds`,
          detail: $localize`:@@todayPoint1Detail:Amount, category, account. You pick the category, because you know what the money was for.`,
        },
        {
          title: $localize`:@@todayPoint2Title:Repeats handle themselves`,
          detail: $localize`:@@todayPoint2Detail:Set up rent, your salary and your subscriptions once. They show up under Upcoming before they land.`,
        },
        {
          title: $localize`:@@todayPoint3Title:Search everything you have added`,
          detail: $localize`:@@todayPoint3Detail:Find any transaction by its note, its category or its amount.`,
        },
      ],
    },
    {
      id: 'plan',
      tab: $localize`:@@tabPlan:Plan`,
      headline: $localize`:@@planHeadline:How is the plan holding up?`,
      body: $localize`:@@planBody:Give each category an amount and the bars show you where each one stands. The period is whatever you actually live in: a month, a fortnight, or the gap between two pay days.`,
      frameLabel: $localize`:@@planFrameLabel:The Plan screen. It shows what is left to spend this period and how each category is doing.`,
      points: [
        {
          title: $localize`:@@planPoint1Title:Keep budgets apart`,
          detail: $localize`:@@planPoint1Detail:The house and a side project do not belong in the same numbers. Switch between them from any screen.`,
        },
        {
          title: $localize`:@@planPoint2Title:See how the period went`,
          detail: $localize`:@@planPoint2Detail:What you planned, what you spent, and where the two stopped matching.`,
        },
        {
          title: $localize`:@@planPoint3Title:No red, no telling off`,
          detail: $localize`:@@planPoint3Detail:Going over is just a line that says how much by. Ledgerly does not use alarm colours anywhere.`,
        },
      ],
    },
    {
      id: 'money',
      tab: $localize`:@@tabMoney:Money`,
      headline: $localize`:@@moneyHeadline:What am I worth today?`,
      body: $localize`:@@moneyBody:Bank, cash and cards in one total. It moves when you add something, and nothing else moves it.`,
      frameLabel: $localize`:@@moneyFrameLabel:The Money screen. It shows what you are worth today, above the accounts that add up to it.`,
      points: [
        {
          title: $localize`:@@moneyPoint1Title:Add as many accounts as you use`,
          detail: $localize`:@@moneyPoint1Detail:Current, cash, savings, credit. You set the starting balance, so it begins where your money really is.`,
        },
        {
          title: $localize`:@@moneyPoint2Title:Moving money is not spending`,
          detail: $localize`:@@moneyPoint2Detail:Send cash from savings to current and Ledgerly counts it as a move, not as an expense.`,
        },
        {
          title: $localize`:@@moneyPoint3Title:Fix a balance in one line`,
          detail: $localize`:@@moneyPoint3Detail:When the app and the real account disagree, say what the balance should be and carry on.`,
        },
      ],
    },
    {
      id: 'tools',
      tab: $localize`:@@tabTools:Tools`,
      headline: $localize`:@@toolsHeadline:Where is everything else?`,
      body: $localize`:@@toolsBody:Settings, and the way out. Your transactions come out as a CSV, and backup copies them to Google Drive, iCloud or a file you keep, once you switch it on.`,
      frameLabel: $localize`:@@toolsFrameLabel:The Tools screen. It shows where you stand with Ledgerly, above the settings and the export options.`,
      points: [
        {
          title: $localize`:@@toolsPoint1Title:Export to CSV`,
          detail: $localize`:@@toolsPoint1Detail:One file, opens in any spreadsheet. It works whether you have paid or not.`,
        },
        {
          title: $localize`:@@toolsPoint2Title:Backup and restore`,
          detail: $localize`:@@toolsPoint2Detail:Google Drive, iCloud or a local file. This is the only thing that copies your data off the phone, and it waits until you turn it on.`,
        },
        {
          title: $localize`:@@toolsPoint3Title:Lock the app`,
          detail: $localize`:@@toolsPoint3Detail:A passcode, plus your fingerprint or face if your phone has it.`,
        },
        {
          title: $localize`:@@toolsPoint4Title:165 currencies, 5 languages`,
          detail: $localize`:@@toolsPoint4Detail:Pick your currency once. The app is in English, French, German, Italian and Spanish, the same five this site is in.`,
        },
      ],
    },
  ];
}
